import { createContext, useState, useEffect, useContext, useMemo } from 'react';
import { useAtomValue } from 'jotai';

import type { LeagueFilterList } from '@/atoms/league-filter';
import { 
	filterAtom,
	isSelectAllCheckedAtom,
	filteredResultAtom,
	oddsLeaguesForFilterAtom
} from '@/atoms/league-filter';

interface LeagueFilterContextType {
	tempSelectAllChecked: boolean;
	setTempSelectAllChecked: React.Dispatch<React.SetStateAction<boolean>>;
	tempSelectedLeagues: LeagueFilterList;
	setTempSelectedLeagues: React.Dispatch<React.SetStateAction<LeagueFilterList>>;
}

const LeagueFilterContext = createContext<LeagueFilterContextType>({} as LeagueFilterContextType);

export const LeagueFilterProvider = ({ 
	children 
}: {
	children: React.ReactNode;
}) => {
	const filter = useAtomValue(filterAtom);
	const hasSportFilter = filter.sport !== 0;

	const isSelectAllChecked = useAtomValue(isSelectAllCheckedAtom);
	const oddsLeagues = useAtomValue(oddsLeaguesForFilterAtom);
	const filteredResult = useAtomValue(filteredResultAtom);
	const [tempSelectAllChecked, setTempSelectAllChecked] = useState(isSelectAllChecked);
	const [tempSelectedLeagues, setTempSelectedLeagues] = useState(filteredResult);

	useEffect(() => {
		setTempSelectedLeagues(leagues => {
			return [...oddsLeagues].map(([leagueId, leagueName]) => ({
				leagueId,
				leagueName,
				checked: Boolean(
					leagues.find(leagueItem => leagueItem.leagueId === leagueId)?.checked
				)
			}));
		});
	}, [oddsLeagues]);

	const value = useMemo(() => ({
		tempSelectAllChecked,
		setTempSelectAllChecked,
		tempSelectedLeagues,
		setTempSelectedLeagues
	}), [tempSelectAllChecked, tempSelectedLeagues]);

	return (
		<LeagueFilterContext.Provider value={value}>
			{hasSportFilter ? children : null}
		</LeagueFilterContext.Provider>
	);
};

export const useLeagueFilterContext = () => useContext(LeagueFilterContext);